import React from "react";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import { Gcp } from "../../types/gcp";
import { Resource } from "../../types/resource";
import { Site } from "../../types/site";
import SiteRectangle from "./SiteRectangle";
import HeavyMachineMarker from "./HeavyMachineMarker";
import Spinner from "../Spinner";
import { Settings } from "../../config/settings";
import GeoSelectionRectangle from "./GeoSelectionRectangle";
import { toDMS } from "../../utils/geoUtils";
import { calcSiteCenter, isSiteRegistered } from "../../utils/siteUtils";
import { Bounds } from "../../types/bounds";
import { estimateZoomLevel } from "../../utils/geoUtils";
import { calcSiteMaxWidth } from "../../utils/siteUtils";

type Props = {
  gcp: Gcp;
  resources: [Resource];
  sites: [Site];
  basePath: string;
  mapCenter: [number, number] | null;
  site: Site;
};

const SiteMap: React.FC<Props> = ({ gcp, resources, sites, basePath, mapCenter, site }) => {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: gcp.apiKey,
    language: "ja",
  });

  const handleBoundsChange = (bounds: Bounds) => {
    const directionFields = [
      { id: "east", value: bounds.east },
      { id: "west", value: bounds.west },
      { id: "north", value: bounds.north },
      { id: "south", value: bounds.south },
    ];

    directionFields.forEach(({ id, value }) => {
      const directionField = document.getElementById(`site_${id}_extremity_raw`) as HTMLInputElement;
      if (directionField) {
        directionField.value = toDMS(value);
      }
    });
  };

  const showedSites = sites.filter((s) => s.eastExtremity && s.westExtremity && s.northExtremity && s.southExtremity);

  const siteWidthMeters = isSiteRegistered(site) ? calcSiteMaxWidth(site) : undefined;
  const mapHeight = window.innerHeight * 0.7;

  const zoom = siteWidthMeters !== undefined ? estimateZoomLevel(siteWidthMeters, mapHeight) : 13;

  const containerStyle = {
    height: "70vh",
    width: "100%",
  };

  // MEMO: 重機→現場→皇居の順で中心位置を決定
  const center = isSiteRegistered(site)
    ? calcSiteCenter(site)
    : {
        lat: mapCenter?.[0] ?? Settings.centerOfMap.lat,
        lng: mapCenter?.[1] ?? Settings.centerOfMap.lon,
      };

  const renderMap = () => (
    <div className="row">
      <div className="col-12">
        <GoogleMap mapContainerStyle={containerStyle} clickableIcons={false} center={center} zoom={zoom}>
          {showedSites.map((s) => (
            <SiteRectangle site={s} key={`site-${s.id}`} basePath={basePath} />
          ))}
          {resources.map((r) => (
            <HeavyMachineMarker resource={r} key={`heavyMachine-${r.id}`} basePath={basePath} />
          ))}

          <GeoSelectionRectangle
            initCenter={center}
            initHeight={0.05}
            initWidth={0.05}
            onBoundsChange={handleBoundsChange}
            site={site}
          />
        </GoogleMap>
      </div>
    </div>
  );

  if (loadError) {
    return <div>Map cannot be loaded right now, sorry.</div>;
  }

  return isLoaded ? renderMap() : <Spinner />;
};

export default SiteMap;
